import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Track } from "@/types";
import { useMusic } from "@/contexts/MusicContext";

interface UseAudioControlsProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
}

export function useAudioControls({
  currentTrack,
  isPlaying,
  setIsPlaying,
}: UseAudioControlsProps) {
  const { toast } = useToast();
  const { isTransitioning } = useMusic();
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isActuallyPlaying, setIsActuallyPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayInProgressRef = useRef(false);

  // Control playback with lock to prevent race conditions
  useEffect(() => {
    // Skip playback control during track transitions
    if (!audioRef.current || isTransitioning) return;

    if (isPlaying) {
      // Check if we have a valid audio source
      const audioSrc = audioRef.current.src;
      if (!audioSrc || audioSrc === window.location.href || audioSrc === "") {
        console.log("⏳ Audio source not ready yet, waiting...");
        return;
      }

      // Prevent duplicate play calls
      if (isPlayInProgressRef.current) {
        console.log("⏳ Play already in progress, skipping...");
        return;
      }

      isPlayInProgressRef.current = true;

      audioRef.current
        .play()
        .then(() => {
          isPlayInProgressRef.current = false;
        })
        .catch((error) => {
          isPlayInProgressRef.current = false;

          // AbortError is expected when switching tracks - don't show error
          if (error?.name === "AbortError") {
            console.log(
              "⏭️ Playback aborted due to track change (expected behavior)",
            );
            return;
          }

          console.error("Playback error:", error);
          setIsPlaying(false);

          const errorMessage =
            error?.name === "NotSupportedError"
              ? "Audio format not supported or URL unavailable"
              : error?.name === "NotAllowedError"
                ? "Playback blocked. Please interact with the page first."
                : "Could not play this track. Please try again.";

          toast({
            title: "Playback error",
            description: errorMessage,
            variant: "destructive",
          });
        });
    } else {
      audioRef.current.pause();
      isPlayInProgressRef.current = false;
    }
  }, [isPlaying, currentTrack, toast, setIsPlaying, isTransitioning]);

  // Reset playback position when track changes
  useEffect(() => {
    if (currentTrack) {
      setCurrentTime(0);
      setIsActuallyPlaying(false);
      isPlayInProgressRef.current = false; // Reset lock for new track
    }
  }, [currentTrack]);

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle seeking
  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  // Handle volume change
  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  // Format time
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return {
    currentTime,
    volume,
    isActuallyPlaying,
    setIsActuallyPlaying,
    audioRef,
    handleTimeUpdate,
    handleSeek,
    handleVolumeChange,
    formatTime,
  };
}
